import React, {
  useCallback,
  useMemo,
} from 'react';
import {
  Controller,
  useFormContext,
} from 'react-hook-form';
import {
  isEmptyString,
} from '@populateui/utils';
import Button from 'pages/Dashboard/components/Button';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import SignatureBox from 'views/EMR/components/FillableForm/components/SignatureBox';
import PreparationStep from 'views/EMR/components/FillableForm/components/PreparationStep';
import useSignaturePrepField from 'views/EMR/components/FillableForm/hooks/useSignaturePrepField';
import {
  FormFieldsData,
} from 'views/EMR/components/FillableForm/types';
import {
  getStableKey,
} from 'views/EMR/components/FillableForm/utils/fieldPositioning';

export type PreparationField = {
  title: string;
  name: 'signature' | 'initial';
  description: string;
  placeholder: string;
};

export type PreparationCompleteData = {
  signature: string;
  initial: string;
};

type Props = {
  fields: PreparationField[];
  onStart: (data: PreparationCompleteData) => void;
};

export default function PreparationSteps({
  fields,
  onStart,
}: Props) {
  const {
    control,
    watch,
    trigger,
    formState: { submitCount },
  } = useFormContext<FormFieldsData>();

  const signaturePrep = useSignaturePrepField('signature');
  const initialPrep = useSignaturePrepField('initial');

  const signature = watch('signature');
  const initial = watch('initial');

  const hasSignatureField = fields.some((field) => field.name === 'signature');
  const hasInitialField = fields.some((field) => field.name === 'initial');

  const canStart = useMemo(
    () => (!hasSignatureField || !isEmptyString(signature))
      && (!hasInitialField || !isEmptyString(initial)),
    [hasSignatureField, signature, hasInitialField, initial],
  );

  const handleStart = useCallback(async () => {
    const fieldNames = fields.map((field) => field.name);
    const valid = await trigger(fieldNames);
    if (!valid) {
      return;
    }
    onStart({
      signature: signature ?? '',
      initial: initial ?? '',
    });
  }, [fields, trigger, signature, initial, onStart]);

  function getPrepForField(name: 'signature' | 'initial') {
    return name === 'signature' ? signaturePrep : initialPrep;
  }

  return (
    <div className="w-full border-b border-gray-200">
      <div className="flex flex-col gap-8 max-w-2xl m-auto p-8 w-full">
        {fields.map((item, index) => {
          const prep = getPrepForField(item.name);
          return (
            <Controller
              key={getStableKey(item)}
              name={item.name}
              rules={{ required: true }}
              control={control}
              render={({ field, fieldState }) => {
                const { invalid } = fieldState;
                const completed = !isEmptyString(field.value);
                const showError = invalid && (prep.interacted || submitCount > 0);
                return (
                  <Wrapper
                    error={showError}
                    id={item.name}
                    showErrorText={false}
                    containerClassName="!cursor-default"
                  >
                    <div className="flex flex-col gap-2 w-full">
                      <PreparationStep
                        completed={completed}
                        stepNumber={index + 1}
                        title={item.title}
                        description={item.description}
                      />
                      <div className="flex flex-col gap-4">
                        <SignatureBox
                          mode={prep.tab}
                          onModeChange={prep.setTab}
                          canvasRef={prep.padRef}
                          boxRef={prep.boxRef}
                          canvasWidth={prep.canvasWidth}
                          canvasHeight={prep.canvasHeight}
                          typedValue={prep.typedValue}
                          onDraw={prep.onDraw}
                          onType={prep.onType}
                          onClear={prep.onClear}
                          setInteracted={prep.setInteracted}
                          isDisabled={false}
                          placeholder={prep.isTyping ? prep.typePlaceholder : ''}
                          inputId={prep.inputId}
                          drawingData={prep.drawingData as never}
                          onSaveDrawingData={prep.setDrawingData}
                        />
                      </div>
                    </div>
                  </Wrapper>
                );
              }}
            />
          );
        })}
        <div className="flex justify-end">
          <Button
            variant="contained"
            color="primary"
            disabled={!canStart}
            onClick={handleStart}
            className="capitalize"
          >
            Start
          </Button>
        </div>
      </div>
    </div>
  );
}
